import { faqsVideos } from "./faqs";
import {
  HypertrophyExerciseLink,
  HypertrophyWorkouts,
  TwelveWeekExercise,
} from "./types";

// Utility to convert "m:ss" or "mm:ss" string to seconds (number)
function parseTimestamp(ts?: string | number): number | undefined {
  if (typeof ts === "number") return ts;
  if (!ts) return undefined;
  const match = ts.match(/^(\d+):([0-5]\d)$/);
  if (match) {
    const [, min, sec] = match;
    return parseInt(min, 10) * 60 + parseInt(sec, 10);
  }
  // fallback: try to parse as number
  const asNum = Number(ts);
  return isNaN(asNum) ? undefined : asNum;
}

const twelveWeekExercisesObject: Record<
  string,
  Omit<TwelveWeekExercise, "slug">
> = {
  "bent-over-row": {
    title: "X3 Bent-Over Row",
    description:
      "The X3 bent-over row targets the lats and posterior deltoids while minimizing arm involvement. Perform this movement with a strong hip hinge and flat back, using a controlled tempo and partial reps to fatigue every range of motion.",
    videoId: "4c76df58074ceeabe57a6f2ae162765f",
    thumbnailTimestampSeconds: 174,
    initialSetup: [
      {
        label:
          "Double the band under the ground plate, centered through the channel.",
        timestamp: "2:15",
      },
      {
        label:
          "Grip the bar with a supinated (underhand) grip; pronated is optional based on comfort.",
        timestamp: "2:28",
      },
      {
        label:
          "Stand shoulder-width apart on top of the ground plate so your mid-foot aligns with the band, with knees slightly bent.",
      },
      {
        label: "Hinge at the hips to ~45° with a flat, neutral spine.",
      },
      {
        label:
          "Let the bar hang below the knees with arms long and elbows pointed back.",
      },
    ],
    formPoints: [
      {
        label:
          "Drive the elbows back to pull the bar toward the beltline, keeping it close to the body.",
      },
      {
        label:
          "Don’t pull into your chest or a back injury can occur. The closer you pull the bar to your hip, the less stress you’re placing directly on your back.",
      },
      {
        label:
          "Focus on initiating the movement with the lats and upper back, not the arms.",
      },
      {
        label:
          "Maintain constant tension and avoid straightening your arms at the bottom of the movement.",
      },
      {
        label:
          "Use a slow tempo (2–3 seconds up and down) to maintain control and maximize tension.",
        timestamp: "2:37",
      },
      {
        label:
          "Strongest range is mid-rep—expect many mid-range partials; then finish with shorter reps into the weak range to total fatigue.",
        timestamp: "2:43",
      },
    ],
    variations: [],
    transcriptPath: "/transcripts/exercises/bent-over-row.md",
  },
  "bicep-curl": {
    title: "X3 Bicep Curl",
    description:
      "The X3 Bicep Curl uses a drag curl motion to isolate the biceps and keep tension high throughout the strongest part of your range of motion. By keeping the bar close to the body and using a slow tempo, this maximizes muscle recruitment.",
    videoId: "04d025d4eadb723181e0579b6e3a7c66",
    thumbnailTimestampSeconds: 118,
    initialSetup: [
      {
        label:
          "Loop the band over both bar hooks and single it over the ground plate’s center channel.",
        timestamp: "1:14",
      },
      {
        label:
          "Grab the bar with a palms-up (supinated) grip; keep your elbows slightly flexed and wrists neutral to maintain constant tension.",
        timestamp: "1:24",
      },
      {
        label:
          "Stand on the ground plate centered over the band with feet shoulder-width apart.",
        timestamp: "1:31",
      },
    ],
    formPoints: [
      {
        label:
          "Curl by flexing your elbows only—keep the bar close to your body; avoid letting your elbows drift forward; move them backward as the bar comes up.",
        timestamp: "2:20",
      },
      {
        label:
          "Use a drag-curl path: slide the bar up along the torso to minimize shoulder involvement; bar travels straight up and down.",
        timestamp: "1:39",
      },
      {
        label:
          "Lift until the bar reaches mid-to-lower chest (full biceps contraction) without letting the elbows move forward.",
        timestamp: "1:54",
      },
      {
        label:
          "Use a slow, controlled cadence—about 2–3 seconds each direction—to maximize time under tension.",
        timestamp: "2:21",
      },
      {
        label: "Maintain constant tension; don’t lock out at the bottom.",
        timestamp: "2:35",
      },
      {
        label:
          "When full-range reps fail, finish with lengthened partials (diminishing range) to complete fatigue.",
        timestamp: "2:55",
      },
    ],
    variations: [],
    transcriptPath: "/transcripts/exercises/bicep-curl.md",
    faqVideo: faqsVideos["x3-bicep-curl-vs-typical-curl"],
    faqVideoTitle: "X3 Bicep Curl vs. Typical Curl",
    faqVideoDescription:
      "This short video explains the science behind the X3 Bicep Curl and why it’s different from traditional variations.",
  },
  "calf-raise": {
    title: "X3 Calf Raise",
    description:
      "The X3 Calf Raise targets the gastrocnemius and soleus with continuous tension and high-rep fatigue. By keeping the band under the ball of the foot and the bar pinned to the torso, you can overload the strong range while minimizing stress in the weak range and improve ankle control and balance.",
    videoId: "3960cd1f5a6809f946ea45d77d192fe6",
    thumbnailTimestampSeconds: 159,
    initialSetup: [
      {
        label:
          "Double the band under the ground plate, centered in the channel, and loop onto both bar hooks.",
      },
      {
        label:
          "Place the ball of each foot directly over the band in the channel with heels hanging off the back of the plate; stand hip-width for balance.",
        timestamp: "2:05",
      },
      {
        label:
          "Start in a deadlift stance, pull up on the bar, and stand tall with control.",
      },
      {
        label:
          "Hold the bar firmly against your body using the trapezius, not your arms.",
        timestamp: "1:57",
      },
      {
        label:
          "Ensure balance before starting, as this exercise challenges stability.",
      },
    ],
    formPoints: [
      {
        label:
          "Keep the bar against the body and contract the trapezius to stabilize the shoulder joint.",
      },
      {
        label:
          "Use higher reps with a lighter band—focus on smooth range and control.",
      },
      {
        label:
          "Hover the heels off the back of the ground plate at the bottom to maintain constant tension (no resting).",
        timestamp: "2:22",
      },
      {
        label:
          "As soon as you can no longer reach the peak point of contraction, finish with lengthened partials (diminishing range) to complete fatigue.",
        timestamp: "2:36",
      },
    ],
    transcriptPath: "/transcripts/exercises/calf-raise.md",
  },
  "chest-press": {
    title: "X3 Chest Press",
    description:
      "The X3 Chest Press is a multi-joint push that safely loads the stronger part of your range while protecting the shoulders at the bottom. Set the band behind your back using the messenger-bag setup, keep constant tension—no slack at the bottom and no lockout at the top—and finish each set with diminishing range as full extension fades.",
    videoId: "be5e58e75569e59309d52b4ccb909f25",
    thumbnailTimestampSeconds: 181,
    initialSetup: [
      {
        label:
          "Hook the band onto both bar hooks and double it; if needed, wrap the band around a hook once or twice to remove slack.",
        timestamp: "2:08",
      },
      {
        label:
          "Form a triangle with the doubled band and bar; bring the band over one shoulder like a messenger strap and rotate the bar across the body.",
        timestamp: "2:30",
      },
      {
        label:
          "Drop the upper side of the band under the rear deltoid, thread the other arm through, and bring the bar to chest height.",
        timestamp: "2:49",
      },
      {
        label:
          "Keep wrists neutral and grip the bar with a pronated (palms-down) hand position; wrap your thumbs around the bar.",
      },
    ],

    formPoints: [
      {
        label:
          "Press forward and slightly downward, thinking about bringing the upper arms toward the body’s midline; stop just short of lockout.",
        timestamp: "3:00",
      },

      {
        label:
          "If you’re feeling most of the exercise in your triceps, keep your elbows back and slightly flared away from your body.",
        timestamp: "3:27",
      },
      {
        label:
          "Maintain constant tension—avoid letting the band go slack at the bottom.",
        timestamp: "3:44",
      },
      {
        label:
          "Use slow, controlled reps (about 2–3 seconds out and 2–3 seconds back).",
      },
      {
        label:
          "As full extension becomes impossible, continue in diminishing range until the last rep is only an inch or so.",
        timestamp: "4:18",
      },
    ],
    transcriptPath: "/transcripts/exercises/chest-press.md",
    faqVideo: faqsVideos["x3-chest-press-positioning-and-band-slip"],
    faqVideoTitle: "Chest Press Setup & Band Positioning",
    faqVideoDescription:
      "This quick video shows how to get into position for the X3 Chest Press while keeping the bands secure. Avoid slippage, improve your range of motion, and keep your muscles engaged.",
  },
  deadlift: {
    title: "X3 Deadlift",
    description:
      "The X3 Deadlift is a powerful posterior chain movement combining elements of traditional and Romanian deadlifts. This variation maximizes overload in the strong range—where muscles generate the most force—while minimizing joint stress in the weaker ranges. By using slow, controlled form and progressing only when capable of 40 full-range reps, users can build strength safely and efficiently across the hamstrings, glutes, and lower back.",
    videoId: "bc32b6a7905bac5a169c0441c0a5a85b",
    thumbnailTimestampSeconds: 394,
    initialSetup: [
      {
        label:
          "Double the band under the ground plate, centered in the middle channel.",
        timestamp: "3:14",
      },
      {
        label:
          "Grip the bar with an overhand grip aligned with your mid-foot and pull the slack out of the band.",
        timestamp: "3:24",
      },
      {
        label:
          "Stand with feet hip-width apart, toes at the front edge of the plate so your mid-foot aligns with the band, and knees slightly bent.",
      },
      {
        label:
          "As you begin the move, ensure your back is straight from your neck to pelvis.",
        timestamp: "3:40",
      },
    ],
    formPoints: [
      {
        label:
          "Keep constant tension: reverse just before the band would slack at the bottom; no bouncing or momentum.",
        timestamp: "3:52",
      },
      {
        label:
          "Keep your head and eyes forward throughout the movement, and don’t turn your head.",
      },
      {
        label:
          "Hinge at the hips and drive the bar up using your glutes as the bar tracks close to the legs the whole time.",
        timestamp: "5:14",
      },
      {
        label:
          "Keep traps engaged so the shoulders don’t slump forward; avoid rounding the back.",
        timestamp: "5:54",
      },
      {
        label:
          "Do not lock out the knees at the top; maintain tension through the entire rep.",
        timestamp: "6:08",
      },
      {
        label:
          "Use a slow, controlled cadence (about 2–3 seconds each way) to stay in control through the weaker range.",
      },
      {
        label:
          "When full-range reps fail, finish with lengthened partials (diminishing range) to reach total fatigue.",
        timestamp: "6:40",
      },
    ],
    transcriptPath: "/transcripts/exercises/deadlift.md",
  },
  "front-squat": {
    title: "X3 Front Squat",
    description:
      "The X3 Front Squat loads the body where you’re strongest while keeping joints safe in the weaker range. Rack the bar across the front of the shoulders with elbows slightly up, move under control, keep constant tension (no knee lockout), and finish with shortened reps to reach full fatigue.",
    videoId: "7602a5fe2e367941aa174fc889632e27",
    thumbnailTimestampSeconds: 211,
    initialSetup: [
      {
        label:
          "Center the singled band in the plate’s channel and loop both ends over the bar hooks.",
        timestamp: "1:10",
      },
      {
        label:
          "Rest the bar across the top of the shoulders; keep elbows slightly forward and up, then stand tall to set it.",
        timestamp: "1:15",
      },
      {
        label:
          "Stand hip- to shoulder-width; set your toes slightly out so your knees and toes track.",

        timestamp: "1:56",
      },
    ],
    formPoints: [
      {
        label:
          "Light fingertip/thumb contact is enough—your trapezius holds the bar, not your hands.",
        timestamp: "1:41",
      },
      {
        label:
          "Keep your torso upright and your chest up; balance the bar across your shoulders.",
        timestamp: "2:02",
      },
      {
        label:
          "Sit straight down and straight up—think ‘sit into a chair’ to keep the path vertical.",
        timestamp: "2:17",
      },
      {
        label: "Track knees with toes; don’t let the knees cave inward.",
        timestamp: "2:44",
      },
      {
        label:
          "Avoid locking out at the top; keep smooth, constant tension throughout the set.",
        timestamp: "3:00",
      },
      {
        label:
          "After 15–40 full reps, shorten the range (lengthened partials) to reach complete fatigue.",
        timestamp: "3:20",
      },
    ],
    transcriptPath: "/transcripts/exercises/front-squat.md",
  },
  "overhead-press": {
    title: "X3 Overhead Press",
    description:
      "The X3 Overhead Press targets the deltoids with constant tension and a head-through-the-window finish—letting you apply the most force in the strong range while reducing shoulder stress at the bottom.",
    videoId: "1ceacf4770a0dc1ce8d5e284232902db",
    thumbnailTimestampSeconds: 158,
    initialSetup: [
      {
        label:
          "Route the singled band through the plate channel so it sits centered under your midfoot.",
        timestamp: "0:46",
      },
      {
        label: "Step onto the plate with feet about shoulder-width apart.",
        timestamp: "0:59",
      },
      {
        label:
          "Squat down, take a pronated grip on the bar, then rotate your wrists—do a one-eighty with your hand position (palms face down at the start and face up toward the ceiling at the top).",
      },
      {
        label:
          "Bring the bar to shoulder height and stand tall to the start position.",
        timestamp: "1:07",
      },
    ],
    formPoints: [
      {
        label:
          "Use a controlled cadence, about 2–3 seconds up and 2–3 seconds down.",
        timestamp: "1:10",
      },
      {
        label:
          "Press up and slightly back to the true top—“head through the window”—so your arms form a rectangle as your shoulders rotate back and your deltoids fully contract.",
        timestamp: "2:45",
      },
      {
        label:
          "Always keep the bar in front of you. Never go behind the head, as this causes damage to the shoulder joint.",
      },
      {
        label:
          "Maintain constant tension: don’t lock out at the top and don’t rest at the bottom.",
        timestamp: "2:02",
      },
      {
        label:
          "Work through strong → mid → weak ranges; as full-range breaks down, use diminishing-range partials to reach complete fatigue.",
        timestamp: "3:22",
      },
      {
        label:
          "Aim for at least 15 slow, controlled reps. If you can’t reach 15 standing, switch to the kneeling variation to reduce overall tension.",
        timestamp: "3:42",
      },
    ],
    transcriptPath: "/transcripts/exercises/overhead-press.md",
    // variations: ["upright-row"],
  },
  "pec-crossover": {
    title: "X3 Pec Crossover",
    description:
      "Optional chest-isolation finisher after chest press. With a band around your upper back, sweep a controlled arc to bring the hands together in front of the sternum. Keep constant tension and finish with diminishing-range partials to reach complete fatigue.",
    videoId: "c11cd306d2ca5a4b3cfd70b936f512fd",
    thumbnailTimestampSeconds: 126,
    initialSetup: [
      {
        label:
          "Wrap a looped band behind your upper back at mid-scapula level, with the strands crossing over your rear deltoids; hold one side of the loop in each hand.",
      },
      {
        label:
          "Take a staggered stance, lean slightly forward, and brace your core with a neutral spine.",
      },
      {
        label:
          "Set your arms out to the sides at chest height with a soft bend in your elbows; wrists neutral, palms facing slightly inward.",
      },
      {
        label:
          "Pre-tension the band (no slack) by stepping forward or adjusting hand spacing before the first rep.",
      },
    ],

    formPoints: [
      {
        label:
          "Sweep your arms in a smooth arc across your body, meeting at your sternum/midline; alternate which arm crosses on top each rep.",
      },
      {
        label:
          "Maintain constant tension—don’t rest at the start or finish; avoid band slack.",
      },
      {
        label:
          "Keep your shoulders down and back (don’t shrug); keep a soft elbow bend so it remains a fly, not a press.",
      },
      {
        label:
          "Use a controlled cadence (about 2–3 seconds each way); move deliberately without momentum.",
      },
      {
        label:
          "Don’t lock out at the finish; stop just shy to keep tension on your pecs.",
      },
      {
        label:
          "Work strong → mid → weak ranges; when full-range breaks down, continue with diminishing-range partials until the band no longer moves forward.",
      },
    ],
    transcriptPath: "/transcripts/exercises/pec-crossover.md",
  },
  "split-squat": {
    title: "X3 Split Squat",
    description:
      "A unilateral lower-body movement that loads the front leg through constant tension while improving balance and hip control. Keep the torso upright, drive through the front foot, and finish with diminishing-range partials once full range breaks down.",
    videoId: "c4eabb5c0d9993efa2821ede36973f7e",
    thumbnailTimestampSeconds: 85,
    initialSetup: [
      {
        label: "Hook the band onto both sides of the bar.",
        timestamp: "1:01",
      },
      {
        label:
          "Step your front foot onto the band so it runs under your front foot near the heel; step the rear foot back into a stable split stance (rear heel high).",
        timestamp: "1:05",
      },
      {
        label:
          "Because the band only runs under one foot, there is no concern it will twist the ankle inward.",
      },
      {
        label:
          "As shown in the X3 front squat, rest the bar across your shoulders, elbows slightly forward and up. Light fingertip/thumb contact is enough—trapezius holds the bar, not the hands.",
        timestamp: "1:09",
      },
      {
        label:
          "Square the hips to the front and brace the core before your first rep. Keep the torso upright and chest up; balance the bar across the shoulders.",
        timestamp: "1:49",
      },
    ],
    formPoints: [
      {
        label:
          "Drive upward, keeping your body weight over the front leg; the rear leg is only for balance, not a prime mover.",
        timestamp: "1:14",
      },
      {
        label:
          "Descend under control until the back knee nearly touches the floor (straight down, not forward).",
        timestamp: "1:37",
      },
      {
        label:
          "Use a controlled cadence—about two seconds up, two seconds down.",
        timestamp: "1:44",
      },
      {
        label:
          "Expect strong core stabilization throughout; keep the torso upright and hips level.",
        timestamp: "1:47",
      },
      {
        label:
          "Aim for 15–40 full-range reps per leg; as fatigue sets in, begin shortening the range.",
        timestamp: "2:24",
      },
      {
        label:
          "Finish with diminishing-range partials to complete fatigue (end with very short reps near the bottom).",
        timestamp: "2:35",
      },
    ],
    transcriptPath: "/transcripts/exercises/split-squat.md",
  },
  "tricep-press": {
    title: "X3 Tricep Press",
    description:
      "Learn how to perform the resistance band Tricep Press with the X3 Home Gym. This movement delivers maximum tricep activation while protecting the elbows with variable resistance.",
    videoId: "27e5d9f308e312d77ab660b3004c86ea",
    thumbnailTimestampSeconds: 122,
    initialSetup: [
      {
        label:
          "Hook the band onto both bar hooks and double it; if needed, wrap the band around a hook once or twice to remove slack.",
      },
      {
        label:
          "Form a triangle with the doubled band and bar; bring the band over one shoulder like a messenger strap and rotate the bar across your body.",
      },
      {
        label:
          "Drop the upper strand of the band under the rear deltoid, thread the other arm through, and bring the bar across the chest. The band will sit a bit higher than where you did for the chest press.",
      },
      {
        label:
          "Stand with feet about shoulder-width and lean slightly forward.",
        timestamp: "1:36",
      },
      {
        label:
          "Start with the bar at about eyebrow/forehead level, elbows tucked close and wrists neutral; thumbs wrapped around the bar.",
        timestamp: "1:26",
      },
    ],

    formPoints: [
      {
        label:
          "Slowly straighten your arms, hinging only at the elbows to press the bar away in a controlled arc; stop just shy of lockout.",
        timestamp: "1:40",
      },
      {
        label:
          "Keep elbows tucked—avoid flaring—to keep the load on the triceps.",
      },
      {
        label:
          "Maintain constant tension by not locking out and not letting the band go slack.",
        timestamp: "1:56",
      },
      {
        label:
          "Perform 15–40 slow, controlled reps (about 2–3 seconds each direction).",
        timestamp: "2:09",
      },
      {
        label:
          "Fatigue the strong range, then continue with diminishing-range partials through mid- and weak-range to reach complete fatigue.",
        timestamp: "2:29",
      },
    ],
    transcriptPath: "/transcripts/exercises/tricep-press.md",
  },
  "upright-row": {
    title: "X3 Upright Row",
    description:
      "A vertical pull alternative for those who can’t perform the overhead press that targets the deltoids and trapezius with constant tension. Keep the bar close, lead with the elbows, and use a slow cadence—finishing with diminishing-range partials as full-range breaks down.",
    videoId: "67ee10fe9ec0f881660e136b5a8f4955",
    thumbnailTimestampSeconds: 108,
    initialSetup: [
      {
        label:
          "Loop the singled band under the center of the ground plate and hook both ends onto the bar.",
        timestamp: "1:24",
      },
      {
        label:
          "Stand centered on the plate with feet hip- to shoulder-width; soften the knees and brace your core.",
        timestamp: "1:30",
      },
      {
        label:
          "Grip the bar evenly just inside shoulder width, wrists neutral, thumbs around; stand tall.",
        timestamp: "1:35",
      },
    ],

    formPoints: [
      {
        label:
          "Initiate by driving the elbows up and slightly out—keep wrists below elbows and the bar close; shoulders stay down and back (don’t shrug early).",
        timestamp: "1:40",
      },
      {
        label:
          "Pull in a vertical path until the bar reaches about mid-chest; no need to go higher.",
        timestamp: "2:04",
      },
      {
        label: "Maintain an upright torso; avoid leaning or using momentum.",
      },
      {
        label:
          "Use a controlled cadence (about 2–3 seconds each direction) and maintain constant tension—no resting at the bottom and no lockout pauses at the top.",
      },
      {
        label:
          "As full-range reps break down, continue with diminishing-range partials to reach complete fatigue.",
        timestamp: "2:33",
      },
    ],
    transcriptPath: "/transcripts/exercises/upright-row.md",
  },
};

// Helper to map timestamp (string) to timestampSeconds (number) for all key moments
function mapKeyMoments(arr?: { label: string; timestamp?: string | number }[]) {
  if (!arr) return arr;
  return arr.map(({ label, timestamp, ...rest }) => ({
    label,
    ...rest,
    timestampSeconds: parseTimestamp(timestamp),
  }));
}

export const twelveWeekExercises: Record<string, TwelveWeekExercise> =
  Object.fromEntries(
    Object.entries(twelveWeekExercisesObject).map(([slug, data]) => [
      slug,
      {
        ...data,
        slug,
        initialSetup: mapKeyMoments(data.initialSetup),
        formPoints: mapKeyMoments(data.formPoints),
      },
    ]),
  );

export const hypertrophyExercisesObject: Record<
  string,
  HypertrophyExerciseLink
> = {
  "chest-press": {
    title: "X3 Chest Press",
    description:
      "Primary chest builder using variable resistance. Keep the elbows slightly flared and drive the upper arms toward the midline to load the pecs; as fatigue builds, the range shortens naturally and you keep working the lengthened end of the motion.",
    hypertrophyWorkout: HypertrophyWorkouts.ChestBack,
    thumbnailTimestampSeconds: 123,
    startTime: 73,
  },
  "bent-over-row": {
    title: "X3 Bent-Over Row",
    description:
      "Lat-dominant pull that pairs with pressing to expand what you can do under fatigue. Maintain a solid hinge and keep pulling—even as the range shortens—to stack additional fatigue points.",
    hypertrophyWorkout: HypertrophyWorkouts.ChestBack,
    thumbnailTimestampSeconds: 275.1,
    startTime: 217,
  },
  "overhead-press": {
    title: "X3 Overhead Press",
    description:
      "A vertical press to build capacity through the shoulders and upper back. It’s primarily a strength stimulus here—followed by delt work that targets hypertrophy.",
    hypertrophyWorkout: HypertrophyWorkouts.ChestBack,
    thumbnailTimestampSeconds: 680.7,
    startTime: 637,
  },
  "band-laterals": {
    title: "X3 Band Laterals",
    description:
      "Target the side delts where they grow best—near the bottom of the motion. Choose the right band, bend the wrists down, and pull out through a tight arc for relentless tension.",
    hypertrophyWorkout: HypertrophyWorkouts.ChestBack,
    thumbnailTimestampSeconds: 905.9,
    startTime: 744,
  },
  "tricep-press": {
    title: "X3 Tricep Press",
    description:
      "Primary triceps strength move. Hinge only at the elbow and extend to load the long head hard at the top—where bands give you the most resistance. Triceps are the majority of upper-arm mass, so we start here.",
    hypertrophyWorkout: HypertrophyWorkouts.ArmsSmallerMuscles,
    thumbnailTimestampSeconds: 61,
    startTime: 5,
  },
  "bicep-curl": {
    title: "X3 Bicep Curl",
    description:
      "Classic drag-curl mechanics: elbows behind the torso, bar traveling close to the body. Top-range tension builds capacity; controlled partials at the end drive size.",
    hypertrophyWorkout: HypertrophyWorkouts.ArmsSmallerMuscles,
    thumbnailTimestampSeconds: 197,
    startTime: 130,
  },
  "overhead-tricep-extension": {
    title: "X3 Overhead Tricep Extension",
    description:
      "Lengthened-range triceps training: reach back, feel the bottom stretch, and extend smoothly. Expect the range to shorten as you fatigue—that’s where growth happens.",
    hypertrophyWorkout: HypertrophyWorkouts.ArmsSmallerMuscles,
    thumbnailTimestampSeconds: 278,
    startTime: 232,
  },
  "seated-calf-raise": {
    title: "X3 Seated Calf Raise",
    description:
      "Calf work built for size: sit low, heels dropping into a deep stretch, bar across the thighs. Drive up to a full contraction, then ride controlled partials as the set closes.",
    hypertrophyWorkout: HypertrophyWorkouts.ArmsSmallerMuscles,
    thumbnailTimestampSeconds: 597,
    startTime: 431,
  },
  "single-leg-squats": {
    title: "X3 Single Leg Squats",
    description:
      "Front-leg driven squat with the back foot for balance only. Stay just shy of lockout and let the range shorten naturally as fatigue climbs to keep the quads loaded.",
    hypertrophyWorkout: HypertrophyWorkouts.LegsLowerBack,
    thumbnailTimestampSeconds: 207,
    startTime: 1,
  },
  deadlift: {
    title: "X3 Deadlift",
    description:
      "Standard X3 deadlift with slow, controlled reps to build the entire posterior chain before moving to lengthened-range work.",
    hypertrophyWorkout: HypertrophyWorkouts.LegsLowerBack,
    thumbnailTimestampSeconds: 290,
    startTime: 221,
  },
  "shallow-squat": {
    title: "X3 Shallow Squat",
    description:
      "Depth below parallel with a lighter load to hammer the quads in their lengthened position—serious stimulus without brutal systemic fatigue.",
    hypertrophyWorkout: HypertrophyWorkouts.LegsLowerBack,
    thumbnailTimestampSeconds: 525,
    startTime: 433,
  },
  "stiff-leg-deadlift": {
    title: "X3 Stiff Leg Deadlift",
    description:
      "Near-locked knees to bias the hamstrings while traps and erectors still work—an ideal antagonist to the shallow squat.",
    hypertrophyWorkout: HypertrophyWorkouts.LegsLowerBack,
    thumbnailTimestampSeconds: 670,
    startTime: 619,
  },
  "seated-wrist-curl": {
    title: "X3 Seated Wrist Curl",
    description:
      "Sit low with forearms on thighs, palms up over the knees, and curl through the full wrist arc; forearm work that actually grows.",
    hypertrophyWorkout: HypertrophyWorkouts.LegsLowerBack,
    thumbnailTimestampSeconds: 759,
    startTime: 700,
  },
};

export const hypertrophyExerciseLinks: HypertrophyExerciseLink[] =
  Object.values(hypertrophyExercisesObject);

// Map hypertrophy workouts to ordered exercise slugs
export const hypertrophyWorkoutsExercises: Record<
  HypertrophyWorkouts,
  string[]
> = (() => {
  const map: Record<HypertrophyWorkouts, string[]> = {
    [HypertrophyWorkouts.ChestBack]: [],
    [HypertrophyWorkouts.ArmsSmallerMuscles]: [],
    [HypertrophyWorkouts.LegsLowerBack]: [],
  };
  hypertrophyExerciseLinks.forEach((exercise) => {
    // Find the slug by reverse lookup in hypertrophyExercisesObject
    const slug = Object.entries(hypertrophyExercisesObject).find(
      ([, v]) => v === exercise,
    )?.[0];
    if (slug) {
      map[exercise.hypertrophyWorkout].push(slug);
    }
  });
  return map;
})();
